import { defineStore } from 'pinia';
import type { SemesterOption } from 'src/modules/School/composables/useSchoolData';

export type SchoolFilterPageKey = 'dashboard' | 'teacherHome' | 'studentHome';

interface SchoolFiltersState {
  semesterByPage: Record<SchoolFilterPageKey, string>;
  selectedSchoolDocumentId: string;
  selectedSemesterDocumentId: string;
  // Full metadata (name/code/start_date/end_date) of the selected semester, so
  // consumers (e.g. invite links) can label it and gate on its end date without
  // re-fetching the semester list.
  selectedSemesterInfo: SemesterOption | null;
  selectedClassDocumentId: string;
  selectedStudentDocumentId: string;
}

export const useSchoolFiltersStore = defineStore('schoolFilters', {
  state: (): SchoolFiltersState => ({
    semesterByPage: {
      dashboard: '',
      teacherHome: '',
      studentHome: '',
    },
    selectedSchoolDocumentId: '',
    selectedSemesterDocumentId: '',
    selectedSemesterInfo: null,
    selectedClassDocumentId: '',
    selectedStudentDocumentId: '',
  }),
  actions: {
    setSemester(page: SchoolFilterPageKey, semesterDocumentId: string) {
      this.semesterByPage[page] = semesterDocumentId;
      this.selectedSemesterDocumentId = semesterDocumentId;
    },
    setSelectedSemesterDocumentId(semesterDocumentId: string) {
      // Classes belong to a semester, so a selection made under the previous
      // semester can't survive the switch.
      if (this.selectedSemesterDocumentId !== semesterDocumentId) {
        this.selectedClassDocumentId = '';
      }
      this.selectedSemesterDocumentId = semesterDocumentId;
      this.semesterByPage.dashboard = semesterDocumentId;
      this.semesterByPage.teacherHome = semesterDocumentId;
      this.semesterByPage.studentHome = semesterDocumentId;
      // Drop stale metadata when the id changes without accompanying info; the
      // drawer reconciles it via setSelectedSemesterInfo once options are loaded.
      if (this.selectedSemesterInfo?.documentId !== semesterDocumentId) {
        this.selectedSemesterInfo = null;
      }
    },
    /** Richer setter used by the drawer: sets both the id and its metadata. */
    setSelectedSemesterInfo(info: SemesterOption | null) {
      this.selectedSemesterInfo = info;
      this.setSelectedSemesterDocumentId(info?.documentId || '');
    },
    setSelectedSchoolDocumentId(schoolDocumentId: string) {
      if (this.selectedSchoolDocumentId === schoolDocumentId) return;
      this.selectedSchoolDocumentId = schoolDocumentId;
      // The semester is a global filter now (not scoped to a school), so it is
      // deliberately preserved when the school changes.
      this.selectedClassDocumentId = '';
      this.selectedStudentDocumentId = '';
    },
    setSelectedClassDocumentId(classDocumentId: string) {
      this.selectedClassDocumentId = classDocumentId;
    },
    setSelectedStudentDocumentId(studentDocumentId: string) {
      this.selectedStudentDocumentId = studentDocumentId;
    },
    resetSchoolFilters() {
      this.semesterByPage.dashboard = '';
      this.semesterByPage.teacherHome = '';
      this.semesterByPage.studentHome = '';
      this.selectedSchoolDocumentId = '';
      this.selectedSemesterDocumentId = '';
      this.selectedSemesterInfo = null;
      this.selectedClassDocumentId = '';
      this.selectedStudentDocumentId = '';
    },
  },
});
